/**
 * Pixel-icon resolution. The sprite in `index.html` carries 64 glyphs from the
 * hackernoon pixel-icon-library (MIT); content tables carry no icon field, so this
 * module is the single place that maps a content id to a glyph.
 *
 * R5's `OfferCard.icon` is a content id, not a glyph name — `iconFor` accepts either.
 *
 * Art-direction rule (2026-08-01 review, D-icons): **every** weapon and **every**
 * passive resolves to a glyph no other weapon/passive uses. A generic fallback is a
 * bug, not a default — `tests` aside, `assertDistinctIcons()` below is the guard.
 */

/** Every symbol id present in the inlined sprite, without the `i-` prefix. */
export const SPRITE_ICONS: ReadonlySet<string> = new Set([
  'angle-left',
  'angle-right',
  'arrow-right',
  'badge-check',
  'bars',
  'bolt',
  'brightness-high',
  'brightness-low',
  'bullet-list',
  'bullhorn',
  'chart-line',
  'check',
  'chevron-down',
  'chevron-up',
  'circle-notch',
  'clock',
  'cog',
  'coins',
  'command',
  'cone',
  'crosshair',
  'copy',
  'crown',
  'dash',
  'disc',
  'download',
  'exclamation-triangle',
  'expand',
  'eye',
  'filter',
  'fire',
  'flag',
  'globe',
  'grid',
  'heart',
  'hockey-mask',
  'home',
  'info-circle',
  'lightbulb',
  'link',
  'location-pin',
  'lock',
  'logout',
  'moon',
  'music',
  'pause',
  'pen-nib',
  'plane-departure',
  'play',
  'refresh',
  'robot',
  'search',
  'seedlings',
  'shapes',
  'share-alt',
  'shuffle',
  'sound-mute',
  'sound-on',
  'sparkles',
  'star',
  'star-crescent',
  'sun',
  'times',
  'trash',
  'trending',
  'trophy',
  'users',
]);

/**
 * §6.3 weapon visual language, one distinct glyph each. Keys are the live
 * `weapons.json` ids — a miss here shows up as a duplicate glyph in the HUD build
 * strip, which is exactly the defect this table fixes.
 */
export const WEAPON_ICONS: Readonly<Record<string, string>> = {
  'weapon.spear-jelly-lance': 'arrow-right', // single piercing lance
  'weapon.krill-scatter': 'sparkles', // pellet fan
  'weapon.barreleye-gaze': 'eye', // tracking gaze
  'weapon.copepod-halo': 'circle-notch', // orbital ring
  'weapon.atolla-nova': 'sun', // radial burst
  'weapon.angler-lantern-mine': 'lightbulb', // baited mine
  'weapon.siphonophore-chain-whip': 'link', // chain
  'weapon.bobtail-scion': 'robot', // minion
  'weapon.brine-pool': 'disc', // ground zone
  'weapon.vampire-shroud': 'moon', // drain aura
  'weapon.gulper-maw': 'expand', // engulfing jaws
  'weapon.loosejaw-searchlight': 'brightness-high', // sweeping beam
};

/**
 * §6.4 passives, one distinct glyph each, keyed to the passive's single stat so the
 * icon reads as *what it does* rather than as decoration.
 */
export const PASSIVE_ICONS: Readonly<Record<string, string>> = {
  'passive.photophore-amplifier': 'bolt', // damage_pct
  'passive.twitch-ganglia': 'clock', // attack_speed_pct
  'passive.current-rider-fin': 'plane-departure', // move_speed_pct
  'passive.bathyal-hull': 'heart', // max_hp_flat
  'passive.chitin-laminate': 'hockey-mask', // armor_flat
  'passive.mote-savant': 'coins', // xp_gain_pct
  'passive.lure-cilia': 'location-pin', // pickup_radius_pct
  'passive.bristlemouth-instinct': 'flag', // crit_chance_pct
  'passive.vipertooth-hone': 'pen-nib', // crit_damage_pct
  'passive.bloomfield-organ': 'grid', // area_pct
  'passive.slow-burn-luciferin': 'fire', // duration_pct
  'passive.jet-siphon': 'trending', // projectile_speed_pct
  'passive.recoil-mantle': 'bullhorn', // knockback_pct
  'passive.grazer-gut': 'seedlings', // regen_hp_per_s
  'passive.slit-gill-reflex': 'refresh', // dash_cooldown_s
  'passive.surge-capacitor': 'star-crescent', // surge_drop_tiers
  'passive.salvo-glands': 'bullet-list', // projectile_count_add
  'passive.symbiont-brood': 'users', // summon_damage_pct
  'passive.thermal-halo': 'brightness-low', // aura_damage_amp_pct
  'passive.overflow-siphon': 'download', // overheal_shield_cap_flat
  'passive.predator-memory': 'search', // kill_streak_damage_pct
  'passive.cold-light-focus': 'filter', // focus_aim_damage_pct
};

/**
 * Evolutions reuse their base weapon's glyph — the slot already double-codes the
 * evolved state with a gold border (§13.1.4), so a second symbol would only add noise.
 */
export const EVOLUTION_ICONS: Readonly<Record<string, string>> = {
  'evolution.kingspear-medusa': 'arrow-right',
  'evolution.krillstorm-battery': 'sparkles',
  'evolution.barreleye-oversight': 'eye',
  'evolution.copepod-corona': 'circle-notch',
  'evolution.atolla-alarm-crown': 'sun',
  'evolution.angler-grand-lure': 'lightbulb',
  'evolution.siphonophore-aggregate': 'link',
  'evolution.bobtail-armada': 'robot',
  'evolution.brine-lake-basin': 'disc',
  'evolution.vampire-abyss-cloak': 'moon',
  'evolution.gulper-worldmaw': 'expand',
  'evolution.loosejaw-eclipse-array': 'brightness-high',
};

/**
 * §13.1.2 loadout rows. Four category emblems, one per carousel, so the row is
 * identifiable before its label is read. None of them doubles as an option glyph
 * below — the row emblem answers "what is this control", the option glyph answers
 * "what is currently selected".
 */
export const LOADOUT_ROW_ICONS: Readonly<Record<string, string>> = {
  arena: 'globe',
  difficulty: 'chart-line',
  policy: 'crosshair',
  active: 'bolt',
  // §9.9.1 starting weapon. `shapes` is the generic "a thing with a form" mark and is
  // owned by no weapon, so it stays clear of the three option glyphs under it.
  starter: 'shapes',
};

/**
 * §05 targeting policies, one distinct glyph each — the same "icon reads as what it
 * does" rule the weapon and passive tables follow. `chevron-down` / `chevron-up`
 * carry lowest/highest HP because the direction *is* the policy.
 */
export const POLICY_ICONS: Readonly<Record<string, string>> = {
  nearest: 'location-pin',
  'nearest-threat': 'exclamation-triangle',
  'lowest-hp': 'chevron-down',
  'highest-hp': 'chevron-up',
  'densest-cluster': 'users',
  'forward-cone': 'cone',
  'boss-priority': 'crown',
  'elite-priority': 'star',
};

/**
 * §13.1.2 active-ability slot. Gulper Maw already owns `expand` as a weapon (§6.3)
 * and keeps it here, so the same thing looks the same in both places.
 */
export const ACTIVE_ICONS: Readonly<Record<string, string>> = {
  'active.dash': 'dash',
  'weapon.gulper-maw': 'expand',
};

/**
 * Difficulty is a ladder, not a set of kinds: Standard reads as "as designed" and
 * every Descent tier shares one escalation glyph, with the roman numeral and the
 * carousel pips carrying the rung. Distinct glyphs here would imply distinct kinds.
 */
export const DIFFICULTY_ICONS: Readonly<Record<string, string>> = {
  'diff.standard': 'badge-check',
  'diff.descent-1': 'fire',
  'diff.descent-2': 'fire',
  'diff.descent-3': 'fire',
};

const KIND_FALLBACK: Readonly<Record<string, string>> = {
  weapon: 'bolt',
  passive: 'star',
  evolution: 'crown',
  active: 'shapes',
};

/**
 * Resolves a glyph name from a sprite name, a content id, or an id prefix.
 * Always returns a name that exists in the sprite.
 */
export function iconFor(key: string, kind?: string): string {
  if (key !== '' && SPRITE_ICONS.has(key)) return key;
  const weapon = WEAPON_ICONS[key];
  if (weapon !== undefined) return weapon;
  const passive = PASSIVE_ICONS[key];
  if (passive !== undefined) return passive;
  const evolution = EVOLUTION_ICONS[key];
  if (evolution !== undefined) return evolution;
  const dot = key.indexOf('.');
  if (dot > 0) {
    const prefix = key.slice(0, dot);
    if (prefix === 'weapon') return 'bolt';
    if (prefix === 'passive') return 'star';
    if (prefix === 'evolution') return 'crown';
    if (prefix === 'active') return 'shapes';
  }
  if (kind !== undefined && KIND_FALLBACK[kind] !== undefined) return KIND_FALLBACK[kind];
  return 'shapes';
}

/**
 * Guard for the "every weapon and passive is visually distinguishable" rule.
 * Returns the offending ids: any glyph missing from the sprite, plus any glyph that
 * two different weapons or two different passives share. Empty ⇒ the tables are sound.
 */
export function iconTableProblems(): string[] {
  const problems: string[] = [];
  const check = (table: Readonly<Record<string, string>>, label: string): void => {
    const seen = new Map<string, string>();
    for (const id of Object.keys(table)) {
      const glyph = table[id];
      if (!SPRITE_ICONS.has(glyph)) problems.push(`${label}:${id} → missing glyph "${glyph}"`);
      const owner = seen.get(glyph);
      if (owner !== undefined) problems.push(`${label}:${id} shares "${glyph}" with ${owner}`);
      else seen.set(glyph, id);
    }
  };
  check(WEAPON_ICONS, 'weapon');
  check(PASSIVE_ICONS, 'passive');
  return problems;
}
